import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtTime, fmtDate, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, StatCard, SectionCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Users, Clock, CalendarDays, Hourglass, LogIn, PlusCircle, CalendarClock, AlertTriangle, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const COLORS = ["#0B132B", "#00A67E", "#FF9F0A", "#FF3B30", "#52525B"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [claimStats, setClaimStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/meta/holidays").then((r) => setHolidays(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "manager") {
      api.get("/expiries/upcoming").then((r) => setExpiries(r.data)).catch(() => {});
      api.get("/claims/summary").then((r) => setClaimStats(r.data)).catch(() => {});
    }
  }, [user]);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = holidays.filter((h) => h.date >= today).slice(0, 4);
  const first = user?.name?.split(" ")[0] || "there";
  const ta = data?.today_attendance || {};
  const attendanceOn = (() => { try { return JSON.parse(localStorage.getItem("branding"))?.attendance_enabled !== false; } catch { return true; } })();

  return (
    <div className="animate-fade-up">
      <PageTitle overline={data?.role === "admin" ? "HR Overview" : data?.role === "manager" ? "Team Overview" : "My Workspace"} title={`Hello, ${first}`}>
        {attendanceOn && (
          <Button variant="outline" onClick={() => navigate("/attendance")} data-testid="quick-clock">
            <LogIn className="h-4 w-4 mr-1.5" /> Clock In/Out
          </Button>
        )}
        <Button onClick={() => navigate("/leaves")} data-testid="quick-leave">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Apply Leave
        </Button>
      </PageTitle>

      {(data?.role === "admin" || data?.role === "manager") && data?.stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Users} label="Active Employees" value={data.stats.total_employees} />
          {attendanceOn && <StatCard icon={Clock} label="Present Today" value={data.stats.present_today} accent="text-success" />}
          <StatCard icon={CalendarDays} label="On Leave Today" value={data.stats.on_leave_today} accent="text-warning" />
          <StatCard icon={Hourglass} label="Pending Approvals" value={data.stats.pending_leaves} accent="text-destructive" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Today attendance */}
        {attendanceOn && (
        <SectionCard title="Today's Attendance" className="lg:col-span-1">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ["Clock In", ta.clock_in],
              ["Clock Out", ta.clock_out],
              ["Break 1 Out", ta.break1_start],
              ["Break 1 In", ta.break1_end],
              ["Break 2 Out", ta.break2_start],
              ["Break 2 In", ta.break2_end],
            ].map(([l, v]) => (
              <div key={l} className="p-3 rounded-md bg-muted/50 border border-border">
                <p className="overline">{l}</p>
                <p className="font-mono-num font-semibold mt-1">{fmtTime(v)}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-sm text-muted-foreground">Work hours today</span>
            <span className="font-heading font-bold text-xl font-mono-num">{(ta.work_hours || 0).toFixed(2)}h</span>
          </div>
        </SectionCard>
        )}

        {/* Leave balances (employee) or Dept dist (admin) */}
        {data?.balances && (
          <SectionCard title="Leave Balances" className="lg:col-span-1">
            <div className="space-y-3">
              {Object.entries(data.balances)
                .filter(([k]) => k !== "Off-in-lieu (OIL)")
                .slice(0, 5)
                .map(([type, b]) => {
                  const pct = b.entitled ? (b.remaining / b.entitled) * 100 : 0;
                  return (
                    <div key={type}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-foreground">{type}</span>
                        <span className="font-mono-num text-muted-foreground">{b.remaining}/{b.entitled}d</span>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  );
                })}
              <div className="flex justify-between items-center pt-2 border-t border-border text-sm">
                <span>Off-in-lieu (OIL)</span>
                <span className="font-mono-num font-semibold">{data.balances["Off-in-lieu (OIL)"]?.remaining || 0}h</span>
              </div>
            </div>
          </SectionCard>
        )}

        {(data?.role === "admin" || data?.role === "manager") && data?.dept_distribution && (
          <SectionCard title="Headcount by Department" className="lg:col-span-1">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.dept_distribution} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="department" width={110} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.dept_distribution.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>
        )}

        {/* Claims awaiting payroll (HR/managers) */}
        {(data?.role === "admin" || data?.role === "manager") && claimStats && (
          <SectionCard title="Claims Overview" className="lg:col-span-1" data-testid="claims-tile">
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => navigate("/claims")} className="text-left p-4 rounded-md border border-border hover:bg-muted/50 transition-colors" data-testid="claims-pending">
                <div className="flex items-center gap-2 text-warning"><Clock className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">Pending Review</span></div>
                <p className="font-heading font-extrabold text-3xl mt-2 font-mono-num">{claimStats.pending_count}</p>
              </button>
              <button onClick={() => navigate("/claims")} className="text-left p-4 rounded-md border border-border hover:bg-muted/50 transition-colors" data-testid="claims-approved">
                <div className="flex items-center gap-2 text-success"><Receipt className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">Approved · Unpaid</span></div>
                <p className="font-heading font-extrabold text-3xl mt-2 font-mono-num">{claimStats.approved_unpaid_count}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{fmtMoney(claimStats.approved_unpaid_total)} awaiting next payroll</p>
              </button>
            </div>
          </SectionCard>
        )}

        {/* Upcoming expiries (HR/managers) */}
        {(data?.role === "admin" || data?.role === "manager") && (
          <SectionCard title="Upcoming Expiries" className="lg:col-span-1" data-testid="expiry-tile">
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {expiries.length === 0 && <p className="text-sm text-muted-foreground">No upcoming pass, passport or probation expiries.</p>}
              {expiries.slice(0, 8).map((x, i) => {
                const urgent = x.days_left <= 30;
                return (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-md hover:bg-muted/50 transition-colors" data-testid={`expiry-item-${i}`}>
                    <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${urgent ? "bg-destructive/10" : "bg-warning/10"}`}>
                      <AlertTriangle className={`h-4 w-4 ${urgent ? "text-destructive" : "text-warning"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{x.employee_name} · {x.label}</p>
                      <p className="overline">{fmtDate(x.expiry_date)}</p>
                    </div>
                    <span className={`text-xs font-mono-num shrink-0 ${urgent ? "text-destructive" : "text-muted-foreground"}`}>{x.days_left}d</span>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* Upcoming holidays */}
        <SectionCard title="Upcoming SG Holidays" className="lg:col-span-1">
          <div className="space-y-2">
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground">No upcoming holidays.</p>}
            {upcoming.map((h) => (
              <div key={h.date} className="flex items-center gap-3 p-2.5 rounded-md hover:bg-muted/50 transition-colors">
                <div className="h-9 w-9 rounded-md bg-destructive/10 flex items-center justify-center shrink-0">
                  <CalendarClock className="h-4 w-4 text-destructive" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{h.name}</p>
                  <p className="overline">{fmtDate(h.date)}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
